import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FieldConfig } from "../../formConfig";

interface AddressSuggestion {
  formattedAddress: string;
}

async function fetchAddressSuggestions(query: string, context?: string): Promise<AddressSuggestion[]> {
  if (!query || query.length < 4) return [];
  try {
    let url = `/api/address-suggest?q=${encodeURIComponent(query)}`;
    if (context) url += `&context=${encodeURIComponent(context)}`;
    const r = await fetch(url);
    if (!r.ok) {
      console.error("address-suggest failed:", r.status);
      return [];
    }
    const ct = r.headers.get("content-type") ?? "";
    if (!ct.includes("application/json")) {
      console.error("address-suggest returned non-JSON:", ct);
      return [];
    }
    const body = await r.json();
    return body.suggestions ?? [];
  } catch (e) {
    console.error("address-suggest error:", e);
    return [];
  }
}

export default function AddressField({ field }: { field: FieldConfig }) {
  const { register, setValue, watch } = useFormContext();
  const value = watch(field.id) as string;
  const contextValue = field.contextField ? (watch(field.contextField) as string) : undefined;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const { onBlur: rhfOnBlur, ...rest } = register(field.id);

  useEffect(() => {
    if (!value || value.length < 4) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchAddressSuggestions(value, contextValue || undefined);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  function pickSuggestion(s: AddressSuggestion) {
    setValue(field.id, s.formattedAddress, { shouldDirty: true, shouldTouch: true });
    setOpen(false);
    setSuggestions([]);
  }

  return (
    <div className="address-field">
      <textarea
        id={field.id}
        autoComplete="nope"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore
        {...rest}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          rhfOnBlur(e);
          blurTimer.current = window.setTimeout(() => setOpen(false), 150);
        }}
        placeholder="Start typing an address…"
      />
      {open && (suggestions.length > 0 || loading) && (
        <div
          className="address-suggestions"
          onMouseDown={(e) => {
            e.preventDefault();
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
        >
          {loading && <div className="address-loading">Searching…</div>}
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={i}
              className="address-suggestion"
              onClick={() => pickSuggestion(s)}
            >
              {s.formattedAddress}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
